/**
 * Group template content: the stored shape, the editor draft, and the
 * conversions between them.
 *
 * Stored (`content` on a template with type 'group', validated by the backend
 * against lib/templateLimits):
 *   { group: { title, color },
 *     item_columns:    [{ id, title, type }],      // from the item board
 *     subitem_columns: [{ id, title, type }],      // from its subitems board
 *     items: [{ key, name, column_values, updates,
 *               subitems: [{ key, name, column_values, updates }] }] }
 * column_values use the same { id, type, value, text, dynamic? } cells as subitem
 * templates (lib/columnValues), updates the same { body, mentions } (lib/updates).
 *
 * Draft (what the editor holds; values keyed by column id, as TemplateEditor does):
 *   { group, itemColumns, subitemColumns,
 *     items: [{ key, name, values, updates, subitems: [{ key, name, values, updates }] }] }
 */
import { parseColumnValue, serializeRowValues } from './columnValues';

/** monday's group colours, as the board shows them. */
export const GROUP_COLORS = Object.freeze([
  '#037f4c', '#00c875', '#9cd326', '#cab641', '#ffcb00', '#784bd1', '#a25ddc',
  '#0086c0', '#579bfc', '#66ccff', '#bb3354', '#e2445c', '#ff158a', '#ff5ac4',
  '#ff642e', '#fdab3d', '#7f5347', '#c4c4c4', '#808080',
]);
export const DEFAULT_GROUP_COLOR = '#579bfc';

let keySeq = 0;
export const newKey = (prefix = 'k') => `${prefix}-${Date.now()}-${keySeq++}`;

const copyUpdates = (updates) =>
  (updates || []).map((u) => ({ body: u.body || '', mentions: (u.mentions || []).map((m) => ({ ...m })) }));

const bareColumns = (columns) => (columns || []).map(({ id, title, type }) => ({ id, title, type }));

export function emptyGroupContent() {
  return {
    group: { title: '', color: DEFAULT_GROUP_COLOR },
    item_columns: [],
    subitem_columns: [],
    items: [],
  };
}

export const emptySubitemDraft = () => ({ key: newKey('s'), name: '', values: {}, updates: [] });
export const emptyItemDraft = () => ({ key: newKey('i'), name: '', values: {}, updates: [], subitems: [] });

/** Stored column_values → draft values keyed by column id (only cells with a value). */
function valuesFromCells(cells, columns) {
  const values = {};
  for (const col of columns || []) {
    const cv = (cells || []).find((v) => String(v.id) === String(col.id));
    if (!cv) continue;
    const parsed = parseColumnValue(cv, col);
    if (parsed !== null && parsed !== undefined) values[col.id] = parsed;
  }
  return values;
}

/** Stored content → editor draft. Missing parts read as empty, so an older or partial content never crashes the editor. */
export function draftFromContent(content) {
  const c = content || emptyGroupContent();
  const itemColumns = bareColumns(c.item_columns);
  const subitemColumns = bareColumns(c.subitem_columns);
  return {
    group: { title: (c.group && c.group.title) || '', color: (c.group && c.group.color) || DEFAULT_GROUP_COLOR },
    itemColumns,
    subitemColumns,
    items: (c.items || []).map((it) => ({
      key: newKey('i'),
      name: it.name || '',
      values: valuesFromCells(it.column_values, itemColumns),
      updates: copyUpdates(it.updates),
      subitems: (it.subitems || []).map((s) => ({
        key: newKey('s'),
        name: s.name || '',
        values: valuesFromCells(s.column_values, subitemColumns),
        updates: copyUpdates(s.updates),
      })),
    })),
  };
}

const keptUpdates = (updates) => (updates || []).filter((u) => u.body && u.body.trim() !== '');

/**
 * Editor draft → stored content. Items and subitems without a name are dropped,
 * like blank rows in the subitem editor; so are updates with an empty body.
 */
export function contentFromDraft(draft) {
  const itemColumns = bareColumns(draft.itemColumns);
  const subitemColumns = bareColumns(draft.subitemColumns);
  return {
    group: { title: String(draft.group.title || '').trim(), color: draft.group.color || DEFAULT_GROUP_COLOR },
    item_columns: itemColumns,
    subitem_columns: subitemColumns,
    items: (draft.items || [])
      .filter((it) => it.name && it.name.trim() !== '')
      .map((it) => ({
        key: it.key,
        name: it.name.trim(),
        column_values: serializeRowValues(draft.itemColumns || [], it.values || {}),
        updates: keptUpdates(it.updates),
        subitems: (it.subitems || [])
          .filter((s) => s.name && s.name.trim() !== '')
          .map((s) => ({
            key: s.key,
            name: s.name.trim(),
            column_values: serializeRowValues(draft.subitemColumns || [], s.values || {}),
            updates: keptUpdates(s.updates),
          })),
      })),
  };
}

/** { items, subitems, updates } for the card and the footer. */
export function countContent(content) {
  const items = (content && content.items) || [];
  let subitems = 0;
  let updates = 0;
  for (const it of items) {
    updates += ((it && it.updates) || []).length;
    for (const s of (it && it.subitems) || []) {
      subitems += 1;
      updates += ((s && s.updates) || []).length;
    }
  }
  return { items: items.length, subitems, updates };
}

/** A deep copy with fresh keys; nothing shared with the source. */
export function duplicateContent(content) {
  const c = content || emptyGroupContent();
  return {
    group: { ...(c.group || { title: '', color: DEFAULT_GROUP_COLOR }) },
    item_columns: bareColumns(c.item_columns),
    subitem_columns: bareColumns(c.subitem_columns),
    items: (c.items || []).map((it) => ({
      key: newKey('i'),
      name: it.name,
      column_values: (it.column_values || []).map((v) => ({ ...v, ...(v.dynamic ? { dynamic: { ...v.dynamic } } : {}) })),
      updates: copyUpdates(it.updates),
      subitems: (it.subitems || []).map((s) => ({
        key: newKey('s'),
        name: s.name,
        column_values: (s.column_values || []).map((v) => ({ ...v, ...(v.dynamic ? { dynamic: { ...v.dynamic } } : {}) })),
        updates: copyUpdates(s.updates),
      })),
    })),
  };
}
